/*
 * Copyright © 2020-2025 XCamera. All Rights Reserved.
 * @Description:
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-10-19 13:32:07
 */

import { useCallback } from 'react'
import { message } from 'antd'
import dayjs from 'dayjs'
import type {
  XCRecognitionResult,
  XCSelectionInfo
} from '../types/contextInterface'
import { XC_RECOGNITION_STATUS } from '../types/contextInterface'
import { useAISecurity } from '../context/AISecurityContext'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { useNavigate } from 'react-router-dom'
import {
  fetchAIRecognitionV2,
  fetchAIRecognitionRecall
} from '../../../actions/aiRecognition'
import { useSensorTracking } from './useSensorTracking'

/**
 * 图片上传相关的hook
 */
export const useSelectedImage = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { reportAISafetyHazardsResultClick } = useSensorTracking()
  const {
    selectedImages,
    setSelectedImages,
    recognitionResult,
    setRecognitionResult,
    setRecognitionStatus,
    userId,
    groupId,
    isGroupVIP,
    setIsBatchMode,
    setBatchProgress
  } = useAISecurity()

  // 从Redux store获取modelName
  const buildingSafetyAIModelName = useAppSelector(
    (state) => state.main.abTestConfig?.buildingSafetyAIModelName
  )

  /**
   * 添加图片
   */
  const addImage = useCallback(
    (images: string[]) => {
      setSelectedImages(images)
      message.success('图片添加成功')
    },
    [setSelectedImages]
  )

  /**
   * 添加多张图片
   */
  const addImages = useCallback(
    (images: string[]) => {
      setSelectedImages(images)
      message.success(`成功添加${images.length}张图片`)
    },
    [setSelectedImages]
  )

  /**
   * 调用识别接口（单张）
   */
  const handleRecognition = useCallback(
    async (
      image: string[],
      userID: string,
      selectionInfo?: XCSelectionInfo[]
    ) => {
      if (!userId || !groupId) {
        message.error('用户或团队信息缺失')
        return
      }

      setRecognitionStatus(XC_RECOGNITION_STATUS.RECOGNIZING)

      // 将selectionInfo转换为number[][]格式
      const bbox: number[][] =
        selectionInfo?.map((info) => [
          info.x,
          info.y,
          info.width,
          info.height
        ]) ?? [] // 将选区信息转换为 Array<Array<number>> 格式
      const formattedUserChoice: any[] = [] // 空的用户选择信息

      try {
        // 上报识别开始埋点
        reportAISafetyHazardsResultClick({
          clickItem: '',
          actionType: 'indentifyBegin',
          infoJson: JSON.stringify(bbox),
          infoJson2: JSON.stringify(formattedUserChoice)
        })

        // 调用actions中的AI识别函数
        const result = await dispatch(
          fetchAIRecognitionV2({
            groupID: groupId.toString(),
            url: image[image.length - 1],
            userID: userID,
            modelName: buildingSafetyAIModelName ?? '3B+10B_v0.3',
            user_choice: formattedUserChoice,
            bbox: bbox
          })
        )

        if (result.code !== 200) {
          message.error('识别失败，请稍后重试')
          setRecognitionStatus(XC_RECOGNITION_STATUS.WAITING)
          return
        }

        const response = result.data as XCRecognitionResult

        // 处理整改期限：如果没有设置，则默认为检测时间+3天
        if (response && !response.deadline) {
          const createTime = response.createTime || dayjs().format('YYYY-MM-DD')
          const deadlineDate = dayjs(createTime).add(3, 'days')
          response.deadline = deadlineDate.format('YYYY-MM-DD')
        }

        setRecognitionResult(response)
        setRecognitionStatus(XC_RECOGNITION_STATUS.COMPLETED)
        // 上报识别成功埋点
        reportAISafetyHazardsResultClick(
          {
            clickItem: '',
            actionType: result.code === 200 ? 'indentifySucc' : 'indentifyFail',
            errorMsg: result.code === 200 ? undefined : result.msg,
            infoJson: JSON.stringify(bbox),
            infoJson2: JSON.stringify(formattedUserChoice)
          },
          response
        )
        result.code !== 200 && message.error(result.msg)
      } catch (err) {
        // 上报识别失败埋点
        reportAISafetyHazardsResultClick({
          clickItem: '',
          actionType: 'indentifyFail',
          errorMsg: err instanceof Error ? err.message : '识别失败',
          taskID: '',
          infoJson: JSON.stringify(bbox),
          infoJson2: JSON.stringify(formattedUserChoice)
        })

        message.error('识别失败，请重试')
        setRecognitionStatus(XC_RECOGNITION_STATUS.WAITING)
      }
    },
    [
      userId,
      groupId,
      buildingSafetyAIModelName,
      setRecognitionResult,
      setRecognitionStatus,
      dispatch,
      reportAISafetyHazardsResultClick
    ]
  )

  /**
   * 批量并发识别
   */
  const handleRecognitionBatch = useCallback(
    async (
      images: string[],
      userID: string,
      onComplete: (res: XCRecognitionResult, index: number) => void,
      selectionInfo?: XCSelectionInfo[]
    ) => {
      if (!userId || !groupId) {
        message.error('用户或团队信息缺失')
        return
      }

      // 批量模式按数量设置
      const isBatch = images.length > 1
      setIsBatchMode(isBatch)
      const currentProgress = {
        total: images.length,
        completed: 0,
        failed: 0,
        inFlight: images.length
      }
      setBatchProgress(currentProgress)

      const bbox: number[][] =
        selectionInfo?.map((info) => [
          info.x,
          info.y,
          info.width,
          info.height
        ]) ?? []
      const formattedUserChoice: any[] = []

      // 并发执行识别请求
      const tasks = images.map((url, index) =>
        dispatch(
          fetchAIRecognitionV2({
            groupID: groupId.toString(),
            url,
            userID,
            modelName: buildingSafetyAIModelName ?? '3B+10B_v0.3',
            user_choice: formattedUserChoice,
            bbox
          })
        )
          .then(async (result) => {
            const isSuccess = result.code === 200
            const response = (result.data || {}) as XCRecognitionResult

            // 处理整改期限
            if (isSuccess && response && !response.deadline) {
              const createTime =
                response.createTime || dayjs().format('YYYY-MM-DD')
              const deadlineDate = dayjs(createTime).add(3, 'days')
              response.deadline = deadlineDate.format('YYYY-MM-DD')
            }

            // 更新进度（使用局部变量避免类型问题和并发丢失）
            currentProgress.completed += isSuccess ? 1 : 0
            currentProgress.failed += isSuccess ? 0 : 1
            currentProgress.inFlight = Math.max(currentProgress.inFlight - 1, 0)
            setBatchProgress({ ...currentProgress })

            // 第一个结果用于驱动单行动画
            if (index === 0) {
              if (isSuccess) {
                setRecognitionResult(response)
                setRecognitionStatus(XC_RECOGNITION_STATUS.COMPLETED)
              } else {
                message.error(result.msg || '识别失败，请稍后重试')
                setRecognitionStatus(XC_RECOGNITION_STATUS.WAITING)
              }
            }

            // 回调给页面做插入
            if (isSuccess) {
              onComplete(response, index)
            } else {
              // 埋点失败
              reportAISafetyHazardsResultClick({
                clickItem: '',
                actionType: 'indentifyFail',
                errorMsg: result.msg,
                taskID: '',
                infoJson: JSON.stringify(bbox),
                infoJson2: JSON.stringify(formattedUserChoice)
              })
            }

            return isSuccess
          })
          .catch((err) => {
            currentProgress.failed += 1
            currentProgress.inFlight = Math.max(currentProgress.inFlight - 1, 0)
            setBatchProgress({ ...currentProgress })

            if (index === 0) {
              setRecognitionStatus(XC_RECOGNITION_STATUS.WAITING)
            }

            reportAISafetyHazardsResultClick({
              clickItem: '',
              actionType: 'indentifyFail',
              errorMsg: err instanceof Error ? err.message : '识别失败',
              taskID: '',
              infoJson: JSON.stringify(bbox),
              infoJson2: JSON.stringify(formattedUserChoice)
            })

            return false
          })
      )

      await Promise.allSettled(tasks)
      setIsBatchMode(false)
    },
    [
      userId,
      groupId,
      buildingSafetyAIModelName,
      dispatch,
      setIsBatchMode,
      setBatchProgress,
      setRecognitionResult,
      setRecognitionStatus,
      reportAISafetyHazardsResultClick
    ]
  )

  /**
   * AI重新识别功能
   */
  const handleAgainGenerate = useCallback(
    async (params: {
      id?: number
      descContent?: string[]
      groupID?: string
      modelName?: string
      url?: string
      userID?: string
    }) => {
      try {
        // 调用重新识别接口
        const result = await dispatch(
          fetchAIRecognitionRecall({
            id: params.id || 0,
            descContent: params.descContent || [],
            groupID: params.groupID || groupId?.toString() || '',
            modelName:
              params.modelName || buildingSafetyAIModelName || '3B+10B_v0.3',
            url: params.url || '',
            userID: params.userID || userId || ''
          })
        )

        if (result.code !== 200) {
          message.error('重新识别失败，请稍后重试')
          setRecognitionStatus(XC_RECOGNITION_STATUS.WAITING)
          return
        }
        const response = result.data as XCRecognitionResult

        // 处理整改期限：如果没有设置，则默认为检测时间+3天
        if (response && !response.deadline) {
          const createTime = response.createTime || dayjs().format('YYYY-MM-DD')
          const deadlineDate = dayjs(createTime).add(3, 'days')
          response.deadline = deadlineDate.format('YYYY-MM-DD')
        }
        // 显示成功消息
        if (result.code === 200) {
          return response
        } else {
          message.error(result.msg || '重新识别失败')
          setRecognitionStatus(XC_RECOGNITION_STATUS.WAITING)
        }
      } catch (err) {
        // 错误处理
        message.error('重新识别失败，请重试')
        setRecognitionStatus(XC_RECOGNITION_STATUS.WAITING)
        throw err
      }
    },
    [userId, groupId, buildingSafetyAIModelName, dispatch]
  )

  /**
   * 选择照片 - 跳转页面选择图片并回调
   */
  const handleSelectPhotos = useCallback(() => {
    // 检查用户和团队信息
    if (!userId || !groupId) {
      message.error('用户或团队信息缺失，请重新登录')
      return
    }

    const max = 5
    navigate(encodeURI(`/photo/picker?from=ai-security&mediaType=0&max=${max}`))
  }, [userId, groupId, dispatch, isGroupVIP])

  return {
    selectedImages,
    addImage,
    addImages,
    handleSelectPhotos,
    handleRecognition,
    handleRecognitionBatch,
    handleAgainGenerate,
    recognitionStatus: useAISecurity().recognitionStatus
  }
}
